import { useLazyQuery } from "@apollo/client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PRODUCT_NAME_QUERY,
  SIMPLIFIED_PRODUCT_ID_QUERY,
} from "../../../apis/taxonomy/queries";
import { HgProduct } from "../../../types/taxonomy";
import { AsyncSearchInput } from "../AsyncSearchInput/AsyncSearchInput";
import { SelectOption } from "../AsyncSearchInput/types";
import {
  DEBOUNCE_DELAY,
  DEFAULT_PLACEHOLDER,
  ERROR_MESSAGE,
  LOADING_MESSAGE,
  MIN_CHAR_MESSAGE,
  MIN_FILTER_LENGTH,
  NO_PRODUCTS_MESSAGE,
} from "./constants";
import { ProductInputProps, ProductOption } from "./types";
import { TAXONOMY_CLIENT } from "../../../utils/consts";
import { GroupedProductAutocomplete } from "./GroupedProductAutocomplete";

interface ProductQueryResponse {
  product: {
    data: HgProduct[];
  };
}

function HgProductToSelectOption(
  product: Omit<HgProduct, "description" | "vendor">
) {
  return { label: product.name, value: product.id.toString() };
}

function convertHgProductsToSelectOptionsArray(
  data?: Omit<HgProduct, "description" | "vendor">[]
): SelectOption[] {
  if (!data || data.length === 0) {
    return [];
  }

  return data.map(HgProductToSelectOption);
}

export const ProductInput: React.FC<ProductInputProps> = ({
  onProductsSelect,
  selectedProductIds = [],
  placeholder,
  showDoneButton,
  autoCompleteProps,
  grouped,
}) => {
  const [currentSelectedProducts, setCurrentSelectedProducts] = useState<
    ProductOption[]
  >([]);

  const [fetchProductsByIds, { loading }] = useLazyQuery<ProductQueryResponse>(
    SIMPLIFIED_PRODUCT_ID_QUERY,
    {
      context: { clientName: TAXONOMY_CLIENT },
    }
  );

  const handleOptionsSelect = useCallback(
    (options: ProductOption[]) => {
      setCurrentSelectedProducts(options);
      onProductsSelect(options);
    },
    [onProductsSelect]
  );

  const needToFetchProductIds = useMemo(() => {
    if (selectedProductIds.length === 0) {
      return false;
    }

    if (currentSelectedProducts.length !== selectedProductIds.length) {
      return true;
    }

    return !selectedProductIds.every((id: number) =>
      currentSelectedProducts.some((product) => Number(product.value) === id)
    );
  }, [selectedProductIds, currentSelectedProducts]);

  useEffect(() => {
    if (selectedProductIds.length === 0) {
      setCurrentSelectedProducts([]);
      return;
    }

    const fetchProducts = async () => {
      const { data } = await fetchProductsByIds({
        variables: {
          ids: selectedProductIds,
        },
      });
      if (data?.product?.data) {
        const options = convertHgProductsToSelectOptionsArray(
          data.product.data
        );
        setCurrentSelectedProducts(options);
      }
    };

    if (needToFetchProductIds) {
      fetchProducts();
    }
  }, [selectedProductIds, needToFetchProductIds, fetchProductsByIds]);

  // ── Grouped mode: vendor hierarchy via server-side search ─────────────
  if (grouped) {
    return (
      <GroupedProductAutocomplete
        selectedOptions={currentSelectedProducts}
        onOptionsSelect={handleOptionsSelect}
        placeholder={placeholder || DEFAULT_PLACEHOLDER}
      />
    );
  }

  // ── Flat mode: existing server-side search ──────────────────────────
  return (
    <AsyncSearchInput<
      ProductOption,
      Omit<HgProduct, "description" | "vendor">[]
    >
      id="products"
      searchKey="name"
      query={PRODUCT_NAME_QUERY}
      clientName={TAXONOMY_CLIENT}
      selectedOptions={currentSelectedProducts}
      onOptionsSelect={handleOptionsSelect}
      convertResponseToOptions={convertHgProductsToSelectOptionsArray}
      responseDataPath="product.data"
      placeholderText={placeholder || DEFAULT_PLACEHOLDER}
      minFilterLength={MIN_FILTER_LENGTH}
      debounceDelay={DEBOUNCE_DELAY}
      errorMessage={ERROR_MESSAGE}
      minCharMessage={MIN_CHAR_MESSAGE}
      noResultsMessage={NO_PRODUCTS_MESSAGE}
      loadingMessage={LOADING_MESSAGE}
      showGlobalLoadingIndicator={loading}
      showDoneButton={showDoneButton}
      autoCompleteProps={autoCompleteProps}
    />
  );
};
